import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Search, Loader2, Camera, Scan, BookOpen, Clock,
  UtensilsCrossed, Star, Check, X, ImagePlus, ChevronLeft,
} from 'lucide-react';
import client from '../api/client';
import BarcodeScanner from './BarcodeScanner';
import { isValidBarcode } from '../lib/openFoodFacts';

export default function FoodPickerSheet({ open, onClose, onFoodSelected }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('history');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [fatsecretResults, setFatsecretResults] = useState([]);
  const [fatsecretLoading, setFatsecretLoading] = useState(false);

  // Selected food state
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedServing, setSelectedServing] = useState(null);
  const [fatsecretDetail, setFatsecretDetail] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // Recent foods
  const [recentFoods, setRecentFoods] = useState([]);
  const [recentFoodsLoading, setRecentFoodsLoading] = useState(false);

  // My Foods
  const [myFoods, setMyFoods] = useState([]);
  const [myFoodsLoading, setMyFoodsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Photo scan
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const [photoFoods, setPhotoFoods] = useState([]);

  // Barcode
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scanningBarcode, setScanningBarcode] = useState(false);

  // Recipes
  const [recipeQuery, setRecipeQuery] = useState('');
  const [recipeResults, setRecipeResults] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeDetail, setRecipeDetail] = useState(null);
  const [recipeDetailLoading, setRecipeDetailLoading] = useState(false);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setActiveTab('history');
      clearFoodState();
      setSearchQuery('');
      setFatsecretResults([]);
      setPhotoResult(null);
      setPhotoFoods([]);
      setSelectedRecipe(null);
      setRecipeDetail(null);
      setRecipeQuery('');
      setRecipeResults([]);
      loadRecentFoods();
      loadMyFoods();
    }
  }, [open]);

  const clearFoodState = () => {
    setSelectedFood(null);
    setSelectedServing(null);
    setFatsecretDetail(null);
    setQuantity(1);
  };

  const loadRecentFoods = async () => {
    setRecentFoodsLoading(true);
    try {
      const res = await client.get('/foods/recent?limit=30&offset=0');
      setRecentFoods(res.data?.foods || []);
    } catch {
      // silent
    } finally {
      setRecentFoodsLoading(false);
    }
  };

  const loadMyFoods = async () => {
    setMyFoodsLoading(true);
    try {
      const res = await client.get('/foods');
      setMyFoods(res.data || []);
    } catch {
      // silent
    } finally {
      setMyFoodsLoading(false);
    }
  };

  // Derived categories for My Foods
  const categories = [...new Set(myFoods.map(f => f.category).filter(Boolean))];
  const filteredFoods = selectedCategory === 'all'
    ? myFoods
    : myFoods.filter(f => f.category === selectedCategory);

  // Autocomplete (200ms debounce)
  const acRef = useRef(0);
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setAutocompleteResults([]); setShowAutocomplete(false); return;
    }
    const id = ++acRef.current;
    const timer = setTimeout(async () => {
      try {
        const res = await client.get(`/foods/fatsecret/autocomplete?query=${encodeURIComponent(searchQuery)}&max_results=8`);
        if (id === acRef.current) { setAutocompleteResults(res.data.suggestions || []); setShowAutocomplete(true); }
      } catch {}
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab]);

  // Full FatSecret search (600ms debounce)
  const searchRef = useRef(0);
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setFatsecretResults([]); setFatsecretLoading(false); return;
    }
    const id = ++searchRef.current;
    setFatsecretLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await client.get(`/foods/fatsecret/search?query=${encodeURIComponent(searchQuery)}&max_results=15&language=en`);
        if (id === searchRef.current) { setFatsecretResults(res.data.results || []); setFatsecretLoading(false); setShowAutocomplete(false); }
      } catch { if (id === searchRef.current) setFatsecretLoading(false); }
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab]);

  const pickAutocomplete = (s) => { setSearchQuery(s); setShowAutocomplete(false); };

  const selectFatsecretFood = async (fsFood) => {
    try {
      const res = await client.get(`/foods/fatsecret/${fsFood.fatsecret_id}`);
      const detail = res.data;
      await client.post('/foods/fatsecret/save', detail);
      const servings = detail.servings || [];
      let serving = servings.find(s => s.is_default) || null;
      if (!serving && fsFood.serving_hint) serving = servings.find(s => s.description === fsFood.serving_hint) || null;
      if (!serving) serving = servings[0] || null;
      setFatsecretDetail(detail);
      setSelectedServing(serving);
      setSelectedFood({
        id: fsFood.fatsecret_id,
        name: fsFood.name || fsFood.food_name,
        brand: fsFood.brand,
        servings,
        serving_size: serving?.metric_amount || serving?.amount || 100,
        serving_unit: serving?.metric_unit || serving?.unit || 'g',
        calories: serving?.calories || fsFood.calories || 0,
        protein: serving?.protein || fsFood.protein || 0,
        carbs: serving?.carbs || fsFood.carbs || 0,
        fat: serving?.fat || fsFood.fat || 0,
      });
      setQuantity(1);
    } catch { toast.error(t('foodPicker.errorLoadFoodDetails')); }
  };

  const handleServingChange = (servingId) => {
    const serving = fatsecretDetail?.servings?.find(s => s.serving_id === servingId);
    if (serving) {
      setSelectedServing(serving);
      setSelectedFood(prev => prev ? {
        ...prev,
        serving_size: serving.metric_amount || serving.amount,
        serving_unit: serving.metric_unit || serving.unit,
        calories: serving.calories,
        protein: serving.protein,
        carbs: serving.carbs,
        fat: serving.fat,
      } : prev);
      setQuantity(1);
    }
  };

  const selectMyFood = (food) => {
    const defaultServing = food.servings?.[0] || null;
    setSelectedFood({
      id: food.id,
      name: food.name,
      brand: food.brand,
      serving_size: defaultServing?.metric_serving_amount || food.serving_size || 100,
      serving_unit: defaultServing?.metric_serving_unit || food.serving_unit || 'g',
      serving_description: defaultServing?.description || food.serving_description || '',
      calories: defaultServing?.calories ?? food.calories ?? 0,
      protein: defaultServing?.protein ?? food.protein ?? 0,
      carbs: defaultServing?.carbs ?? food.carbs ?? 0,
      fat: defaultServing?.fat ?? food.fat ?? 0,
      servings: food.servings || [],
    });
    setFatsecretDetail(food.servings?.length ? { servings: food.servings } : null);
    setSelectedServing(defaultServing);
    setQuantity(1);
  };

  const selectRecentFood = (rf) => {
    const servings = rf.servings || 1;
    setSelectedFood({
      id: rf.food_id,
      name: rf.food_name,
      serving_size: rf.serving_size || 100,
      serving_unit: rf.serving_unit || 'g',
      calories: Math.round(rf.calories / servings),
      protein: +(rf.protein / servings).toFixed(1),
      carbs: +(rf.carbs / servings).toFixed(1),
      fat: +(rf.fat / servings).toFixed(1),
      servings: [],
      _fromHistory: true,
    });
    setFatsecretDetail(null);
    setSelectedServing(null);
    setQuantity(1);
  };

  // Photo scan
  const handlePhotoScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    setPhotoResult(null);
    setPhotoFoods([]);
    clearFoodState();
    try {
      const { compressImage } = await import('../utils/compressImage');
      const compressedDataUrl = await compressImage(file, { maxDimension: 1024, quality: 0.6 });
      const base64 = compressedDataUrl.split(',')[1];
      try {
        const res = await client.post('/foods/fatsecret/image-recognition', { image_data: base64, language: 'en', region: 'US' });
        const data = res.data;
        setPhotoResult({
          food_name: data.foods?.map(f => f.food_entry_name || f.food_name).join(', ') || 'Detected Foods',
          total_calories: data.total_calories,
          total_protein: data.total_protein,
          total_carbs: data.total_carbs,
          total_fat: data.total_fat,
          food_count: data.food_count,
        });
        setPhotoFoods(data.foods || []);
        if (data.foods?.length === 1) selectPhotoFood(data.foods[0]);
        toast.success(t(data.food_count === 1 ? 'foodPicker.successDetected_one' : 'foodPicker.successDetected_other', { count: data.food_count }));
      } catch {
        try {
          const aiRes = await client.post('/foods/analyze-photo', { image_data: base64 });
          const ai = aiRes.data;
          const aiFood = {
            fatsecret_id: ai.food_id || `ai-${Date.now()}`,
            food_entry_name: ai.food_name || 'Detected Food',
            food_name: ai.food_name || 'Detected Food',
            calories: ai.calories || 0, protein: ai.protein || 0,
            carbs: ai.carbs || 0, fat: ai.fat || 0,
            serving_size: ai.serving_size || 100, serving_unit: ai.serving_unit || 'g',
            eaten: { total_metric_amount: ai.serving_size || 100, metric_description: ai.serving_unit || 'g' },
            servings: [], is_custom: true,
          };
          setPhotoResult({ food_name: ai.food_name || 'Detected Food', total_calories: ai.calories || 0, total_protein: ai.protein || 0, total_carbs: ai.carbs || 0, total_fat: ai.fat || 0, food_count: 1, source: 'ai_vision' });
          setPhotoFoods([aiFood]);
          selectPhotoFood(aiFood);
          toast.success(t('foodPicker.successAiVision'));
        } catch {
          toast.error(t('foodPicker.errorPhotoAnalysis'));
        }
      }
    } catch {
      toast.error(t('foodPicker.errorReadImage'));
    } finally {
      setPhotoLoading(false);
      document.querySelectorAll('input[type="file"][accept="image/*"]').forEach(inp => { inp.value = ''; });
    }
  };

  const selectPhotoFood = (food) => {
    setSelectedFood({
      id: food.fatsecret_id,
      name: food.food_entry_name || food.food_name,
      serving_size: food.serving_size || food.eaten?.total_metric_amount || 100,
      serving_unit: food.serving_unit || food.eaten?.metric_description || 'g',
      calories: food.calories || 0,
      protein: food.protein || 0,
      carbs: food.carbs || 0,
      fat: food.fat || 0,
      servings: food.servings || [],
    });
    if (food.servings?.length) { setSelectedServing(food.servings[0]); setFatsecretDetail({ servings: food.servings }); }
    setQuantity(1);
  };

  // Barcode
  const handleBarcodeScanned = async (barcode) => {
    if (!isValidBarcode(barcode)) { toast.error(t('foodPicker.errorInvalidBarcode')); return; }
    setScanningBarcode(true);
    setShowBarcodeScanner(false);
    toast.loading(t('foodPicker.lookingUpProduct'), { id: 'barcode-scan' });
    try {
      const res = await client.post('/foods/fatsecret/barcode', { barcode, language: 'en', region: 'US' });
      if (res.data.found && res.data.food) {
        const food = res.data.food;
        toast.success(t('foodPicker.successProductFound'), { id: 'barcode-scan' });
        setSelectedFood({
          id: food.food_id || `scanned_${barcode}`,
          name: food.name,
          brand: food.brand || '',
          serving_size: parseFloat(food.serving_size) || 100,
          serving_unit: food.serving_unit || 'g',
          calories: food.calories || 0,
          protein: food.protein || 0,
          carbs: food.carbs || 0,
          fat: food.fat || 0,
          servings: food.servings || [],
        });
        if (food.servings?.length) { setSelectedServing(food.servings[0]); setFatsecretDetail(food); }
        setQuantity(1);
      } else {
        toast.error(t('foodPicker.errorProductNotFound'), { id: 'barcode-scan' });
      }
    } catch {
      toast.error(t('foodPicker.errorBarcodeLookup'), { id: 'barcode-scan' });
    } finally {
      setScanningBarcode(false);
    }
  };

  // Recipes
  const searchRecipes = async (query) => {
    setRecipeLoading(true);
    try {
      const res = await client.get(`/recipes/search?query=${encodeURIComponent(query || '')}&max_results=20`);
      setRecipeResults(res.data.results || []);
    } catch { toast.error(t('foodPicker.errorSearchRecipes')); }
    finally { setRecipeLoading(false); }
  };

  const openRecipeDetail = async (recipe) => {
    setSelectedRecipe(recipe);
    setRecipeDetailLoading(true);
    try {
      const res = await client.get(`/recipes/${recipe.recipe_id}`);
      setRecipeDetail(res.data);
    } catch { toast.error(t('foodPicker.errorLoadRecipe')); }
    finally { setRecipeDetailLoading(false); }
  };

  const handleAddFood = () => {
    if (!selectedFood) return;
    const servingSize = (selectedServing?.metric_serving_amount ?? selectedServing?.metric_amount) || selectedFood.serving_size || 100;
    const servingUnit = (selectedServing?.metric_serving_unit ?? selectedServing?.metric_unit) || selectedFood.serving_unit || 'g';
    onFoodSelected({
      food_name: selectedFood.name || '',
      serving_size: Number(servingSize) || 100,
      serving_unit: servingUnit || 'g',
      serving_description: selectedServing?.description || '',
      servings: Number(quantity) || 1,
      calories: Number(selectedFood.calories) || 0,
      protein: Number(selectedFood.protein) || 0,
      carbs: Number(selectedFood.carbs) || 0,
      fat: Number(selectedFood.fat) || 0,
    });
    clearFoodState();
    setSearchQuery('');
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center gap-3 shrink-0">
          <Button variant="ghost" size="icon" tooltip={t('common.back')} onClick={onClose} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="font-bold text-base flex-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('foodPicker.addFoodPrefix')} <span className="text-primary">{t('foodPicker.addFoodWord')}</span>
          </h2>
        </div>

        {/* Search bar (for search tab) + Tab bar */}
        <div className="shrink-0 px-4 pt-3 pb-2 space-y-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); if (e.target.value) setActiveTab('search'); }}
              placeholder={t('foodPicker.searchPlaceholder')}
              className="pl-8 text-sm"
            />
            {fatsecretLoading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            {showAutocomplete && autocompleteResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-20 bg-background border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                {autocompleteResults.map((s, i) => (
                  <button key={i} onClick={() => pickAutocomplete(s)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted active:bg-muted transition-colors flex items-center gap-2 border-b border-border/30 last:border-0">
                    <Search className="h-3 w-3 text-muted-foreground shrink-0" />
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); clearFoodState(); setPhotoResult(null); setPhotoFoods([]); setSelectedRecipe(null); setRecipeDetail(null); }}>
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-11 md:h-8">
              <TabsTrigger value="history" className="text-xs px-1 gap-1"><Clock className="h-3.5 w-3.5" /> {t('foodPicker.tabHistory')}</TabsTrigger>
              <TabsTrigger value="myfoods" className="text-xs px-1 gap-1"><UtensilsCrossed className="h-3.5 w-3.5" /> {t('foodPicker.tabMyFoods')}</TabsTrigger>
              <TabsTrigger value="search" className="text-xs px-1 gap-1"><Search className="h-3.5 w-3.5" /> {t('foodPicker.tabSearch')}</TabsTrigger>
              <TabsTrigger value="photo" className="text-xs px-1 gap-1"><Camera className="h-3.5 w-3.5" /> {t('foodPicker.tabPhoto')}</TabsTrigger>
              <TabsTrigger value="scan" className="text-xs px-1 gap-1"><Scan className="h-3.5 w-3.5" /> {t('foodPicker.tabScan')}</TabsTrigger>
              <TabsTrigger value="recipes" className="text-xs px-1 gap-1"><BookOpen className="h-3.5 w-3.5" /> {t('foodPicker.tabRecipes')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto px-4 py-2">

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div className="space-y-1">
              {recentFoodsLoading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
              {!recentFoodsLoading && recentFoods.length === 0 && (
                <div className="text-center py-10 text-muted-foreground">
                  <Clock className="h-10 w-10 mx-auto mb-3 opacity-25" />
                  <p className="text-sm font-medium">{t('foodPicker.noRecentFoods')}</p>
                  <p className="text-xs mt-1">{t('foodPicker.recentFoodsHint')}</p>
                </div>
              )}
              {recentFoods.map((rf, idx) => (
                <div key={`${rf.food_name}-${idx}`}
                  className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.name === rf.food_name && selectedFood?._fromHistory ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                  onClick={() => {
                    if (selectedFood?.name === rf.food_name && selectedFood?._fromHistory) { clearFoodState(); return; }
                    selectRecentFood(rf);
                  }}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{rf.food_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {rf.serving_size || 100}{rf.serving_unit || 'g'} {t('foodPicker.perServing')} · {Math.round(rf.calories)} kcal · P:{Math.round(rf.protein)}g C:{Math.round(rf.carbs)}g F:{Math.round(rf.fat)}g
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs h-5 shrink-0 ml-2">x{rf.log_count}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* MY FOODS TAB */}
          {activeTab === 'myfoods' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                <Badge variant={selectedCategory === 'all' ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory('all')}>{t('foodPicker.categoryAll')}</Badge>
                {categories.map(cat => (
                  <Badge key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory(cat)}>{cat}</Badge>
                ))}
              </div>
              {myFoodsLoading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
              {!myFoodsLoading && filteredFoods.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <UtensilsCrossed className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm">{t('foodPicker.noFoodsSaved')}</p>
                  <p className="text-xs mt-1">{t('foodPicker.myFoodsHint')}</p>
                </div>
              )}
              <div className="space-y-1">
                {filteredFoods.map(food => (
                  <div key={food.id}
                    className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.id === food.id ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                    onClick={() => selectMyFood(food)}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{food.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {food.serving_description || (food.serving_size != null && food.serving_unit ? `${food.serving_size}${food.serving_unit}` : 'serving')} · {food.calories} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                        </p>
                      </div>
                      {food.source === 'fatsecret' && <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">FatSecret</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SEARCH TAB */}
          {activeTab === 'search' && (
            <div className="space-y-1">
              {searchQuery && searchQuery.length >= 2 ? (
                <>
                  {fatsecretResults.length > 0 && (
                    <>
                      <p className="text-xs text-muted-foreground px-2 py-1 uppercase tracking-wide font-semibold flex items-center gap-1">
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" /> {t('foodPicker.bestMatch')}
                      </p>
                      <div
                        className={`p-3 rounded-lg cursor-pointer transition-colors border-2 ${selectedFood?.id === fatsecretResults[0].fatsecret_id ? 'bg-primary/10 border-primary' : 'border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10'}`}
                        onClick={() => selectFatsecretFood(fatsecretResults[0])}>
                        <div className="flex items-center gap-2.5">
                          {fatsecretResults[0].image_url && (
                            <img src={fatsecretResults[0].image_url_small || fatsecretResults[0].image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 border border-border/50" loading="lazy" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm flex items-center gap-1.5 truncate">
                              <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500 shrink-0" />
                              {fatsecretResults[0].name}
                              {fatsecretResults[0].brand && <span className="text-muted-foreground font-normal text-xs">({fatsecretResults[0].brand})</span>}
                            </p>
                            <p className="text-xs text-muted-foreground ml-5">
                              {fatsecretResults[0].serving_hint && `${fatsecretResults[0].serving_hint} · `}{fatsecretResults[0].calories} kcal · P:{fatsecretResults[0].protein}g C:{fatsecretResults[0].carbs}g F:{fatsecretResults[0].fat}g
                            </p>
                          </div>
                        </div>
                      </div>
                      {fatsecretResults.length > 1 && (
                        <p className="text-xs text-muted-foreground px-2 py-1 uppercase tracking-wide font-semibold mt-2 flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> {t('foodPicker.moreResults')}
                        </p>
                      )}
                      {fatsecretResults.slice(1).map(fs => (
                        <div key={fs.fatsecret_id}
                          className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.id === fs.fatsecret_id ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                          onClick={() => selectFatsecretFood(fs)}>
                          <div className="flex items-center gap-2.5">
                            {fs.image_url && <img src={fs.image_url_small || fs.image_url} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0 border border-border/50" loading="lazy" />}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{fs.name} {fs.brand && <span className="text-muted-foreground font-normal">({fs.brand})</span>}</p>
                              <p className="text-xs text-muted-foreground">
                                {fs.serving_hint && `${fs.serving_hint} · `}{fs.calories} kcal · P:{fs.protein}g C:{fs.carbs}g F:{fs.fat}g
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {fatsecretLoading && <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('foodPicker.searchingFatSecret')}</div>}
                  {!fatsecretLoading && fatsecretResults.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-25" />
                      <p className="text-sm">{t('foodPicker.noResultsFor', { query: searchQuery })}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-10 text-muted-foreground">
                  <Search className="h-10 w-10 mx-auto mb-3 opacity-25" />
                  <p className="text-sm font-medium">{t('foodPicker.searchHeading')}</p>
                  <p className="text-xs mt-1">{t('foodPicker.searchSubtext')}</p>
                </div>
              )}
            </div>
          )}

          {/* PHOTO TAB */}
          {activeTab === 'photo' && (
            <div className="space-y-4">
              {!photoResult && !photoLoading && (
                <div className="flex flex-col items-center justify-center py-8 gap-4">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                    <Camera className="h-12 w-12 text-primary" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium">{t('foodPicker.photoHeading')}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('foodPicker.photoSubtext')}</p>
                  </div>
                  <div className="flex gap-3">
                    <label htmlFor="fps-photo-camera" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition-colors">
                        <Camera className="h-4 w-4" /> {t('foodPicker.camera')}
                      </div>
                      <input id="fps-photo-camera" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoScan} />
                    </label>
                    <label htmlFor="fps-photo-gallery" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-5 py-2.5 border border-primary text-primary hover:bg-primary/90 rounded-lg font-medium text-sm transition-colors">
                        <ImagePlus className="h-4 w-4" /> {t('foodPicker.gallery')}
                      </div>
                      <input id="fps-photo-gallery" type="file" accept="image/*" className="sr-only" onChange={handlePhotoScan} />
                    </label>
                  </div>
                </div>
              )}
              {photoLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  <p className="text-sm font-medium">{t('foodPicker.analyzingMeal')}</p>
                  <p className="text-xs text-muted-foreground">{t('foodPicker.analyzingSubtext')}</p>
                </div>
              )}
              {photoResult && !photoLoading && (
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-base">{t(photoResult.food_count === 1 ? 'foodPicker.foodDetected_one' : 'foodPicker.foodDetected_other', { count: photoResult.food_count })}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('foodPicker.tapToSelect')}</p>
                    </div>
                    <Badge className={`${photoResult.source === 'ai_vision' ? 'bg-purple-500/10 text-purple-600 border-purple-500/30' : 'bg-green-500/10 text-green-600 border-green-500/30'} text-xs shrink-0 ml-2`} variant="outline">
                      {photoResult.source === 'ai_vision' ? 'AI Vision' : 'FatSecret'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="rounded-xl p-3 bg-primary/10 text-center"><p className="text-lg font-black text-primary">{Math.round(photoResult.total_calories)}</p><p className="text-xs text-muted-foreground uppercase">kcal</p></div>
                    <div className="rounded-xl p-3 bg-red-500/10 text-center"><p className="text-lg font-black text-red-500">{Math.round(photoResult.total_protein)}g</p><p className="text-xs text-muted-foreground uppercase">protein</p></div>
                    <div className="rounded-xl p-3 bg-yellow-500/10 text-center"><p className="text-lg font-black text-yellow-500">{Math.round(photoResult.total_carbs)}g</p><p className="text-xs text-muted-foreground uppercase">carbs</p></div>
                    <div className="rounded-xl p-3 bg-blue-500/10 text-center"><p className="text-lg font-black text-blue-500">{Math.round(photoResult.total_fat)}g</p><p className="text-xs text-muted-foreground uppercase">fat</p></div>
                  </div>
                  {photoFoods.length > 0 && (
                    <div className="rounded-xl border border-border/50 overflow-hidden">
                      <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('foodPicker.detectedFoods')}</p>
                      </div>
                      <div className="divide-y divide-border/30">
                        {photoFoods.map((food, i) => (
                          <div key={i}
                            className={`px-3 py-2.5 cursor-pointer transition-colors ${selectedFood?.id === food.fatsecret_id ? 'bg-primary/10' : 'hover:bg-muted/40'}`}
                            onClick={() => selectPhotoFood(food)}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{food.food_entry_name || food.food_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {food.eaten?.total_metric_amount ? `${Math.round(food.eaten.total_metric_amount)}${food.eaten?.metric_description || 'g'}` : ''} · {Math.round(food.calories)} kcal · P:{Math.round(food.protein)}g C:{Math.round(food.carbs)}g F:{Math.round(food.fat)}g
                                </p>
                              </div>
                              {selectedFood?.id === food.fatsecret_id && <Check className="h-4 w-4 text-primary shrink-0" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <label htmlFor="fps-retry-cam" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-muted active:bg-muted transition-colors"><Camera className="h-4 w-4" /> {t('foodPicker.camera')}</div>
                      <input id="fps-retry-cam" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoScan} />
                    </label>
                    <label htmlFor="fps-retry-gal" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-muted active:bg-muted transition-colors"><ImagePlus className="h-4 w-4" /> {t('foodPicker.gallery')}</div>
                      <input id="fps-retry-gal" type="file" accept="image/*" className="sr-only" onChange={handlePhotoScan} />
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SCAN TAB */}
          {activeTab === 'scan' && (
            <div className="flex flex-col items-center justify-center py-10 text-center space-y-5">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Scan className="h-12 w-12 text-primary" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-semibold">{t('foodPicker.scanBarcode')}</h3>
                <p className="text-xs text-muted-foreground max-w-sm">{t('foodPicker.scanSubtext')}</p>
              </div>
              {scanningBarcode ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" /> {t('foodPicker.lookingUpProduct')}
                </div>
              ) : (
                <Button onClick={() => setShowBarcodeScanner(true)} className="bg-primary hover:bg-primary/90">
                  <Camera className="mr-2 h-4 w-4" /> {t('foodPicker.scanBarcode')}
                </Button>
              )}
            </div>
          )}

          {/* RECIPES TAB */}
          {activeTab === 'recipes' && (
            <div className="space-y-3">
              {!selectedRecipe ? (
                <>
                  <div className="flex gap-2">
                    <Input
                      value={recipeQuery}
                      onChange={e => setRecipeQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') searchRecipes(recipeQuery); }}
                      placeholder={t('foodPicker.recipesPlaceholder')}
                      className="h-9 text-sm"
                    />
                    <Button size="sm" onClick={() => searchRecipes(recipeQuery)} disabled={recipeLoading} className="bg-primary hover:bg-primary/90 h-9 px-4">
                      {recipeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                  {recipeResults.length > 0 ? (
                    <div className="space-y-2">
                      {recipeResults.map(recipe => (
                        <div key={recipe.recipe_id}
                          className="p-3 rounded-lg bg-muted/30 hover:bg-muted/60 cursor-pointer transition-colors border border-border/30"
                          onClick={() => openRecipeDetail(recipe)}>
                          <div className="flex gap-3">
                            {recipe.image && <img src={recipe.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" loading="lazy" />}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">{recipe.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{recipe.description}</p>
                              <div className="flex items-center gap-3 mt-1.5 text-xs">
                                <span className="font-medium text-primary">{Math.round(recipe.calories)} kcal</span>
                                <span>P:{Math.round(recipe.protein)}g</span>
                                <span>C:{Math.round(recipe.carbs)}g</span>
                                <span>F:{Math.round(recipe.fat)}g</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : !recipeLoading ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-25" />
                      <p className="text-sm font-medium">{t('foodPicker.recipesHeading')}</p>
                      <p className="text-xs mt-1">{t('foodPicker.recipesSubtext')}</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> {t('foodPicker.searchingRecipes')}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <button className="flex items-center gap-1 text-sm text-primary hover:underline"
                    onClick={() => { setSelectedRecipe(null); setRecipeDetail(null); }}>
                    <ChevronLeft className="h-4 w-4" /> {t('foodPicker.backToResults')}
                  </button>
                  {recipeDetailLoading ? (
                    <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('foodPicker.loadingRecipe')}</div>
                  ) : recipeDetail ? (
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        {recipeDetail.image && <img src={recipeDetail.image} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />}
                        <div>
                          <p className="font-bold text-base">{recipeDetail.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{recipeDetail.description}</p>
                          <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                            {recipeDetail.prep_time && <span>{t('foodPicker.prepTime')} {recipeDetail.prep_time}min</span>}
                            {recipeDetail.cook_time && <span>{t('foodPicker.cookTime')} {recipeDetail.cook_time}min</span>}
                            {recipeDetail.servings && <span>{t('foodPicker.serves')} {recipeDetail.servings}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="rounded-xl p-2 bg-primary/10 text-center"><p className="text-sm font-bold text-primary">{Math.round(recipeDetail.nutrition?.calories || 0)}</p><p className="text-xs text-muted-foreground uppercase">kcal</p></div>
                        <div className="rounded-xl p-2 bg-red-500/10 text-center"><p className="text-sm font-bold text-red-500">{Math.round(recipeDetail.nutrition?.protein || 0)}g</p><p className="text-xs text-muted-foreground uppercase">protein</p></div>
                        <div className="rounded-xl p-2 bg-yellow-500/10 text-center"><p className="text-sm font-bold text-yellow-500">{Math.round(recipeDetail.nutrition?.carbs || 0)}g</p><p className="text-xs text-muted-foreground uppercase">carbs</p></div>
                        <div className="rounded-xl p-2 bg-blue-500/10 text-center"><p className="text-sm font-bold text-blue-500">{Math.round(recipeDetail.nutrition?.fat || 0)}g</p><p className="text-xs text-muted-foreground uppercase">fat</p></div>
                      </div>
                      {recipeDetail.ingredients?.length > 0 && (
                        <div className="rounded-xl border border-border/50 overflow-hidden">
                          <div className="px-3 py-2 bg-muted/30 border-b border-border/50"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('foodPicker.ingredients')}</p></div>
                          <div className="divide-y divide-border/30">
                            {recipeDetail.ingredients.map((ing, i) => (
                              <div key={i} className="px-3 py-2 text-xs">{ing.ingredient_description || `${ing.number_of_units || ''} ${ing.measurement_description || ''} ${ing.food_name}`.trim()}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      {recipeDetail.directions?.length > 0 && (
                        <div className="rounded-xl border border-border/50 overflow-hidden">
                          <div className="px-3 py-2 bg-muted/30 border-b border-border/50"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('foodPicker.directions')}</p></div>
                          <div className="divide-y divide-border/30">
                            {recipeDetail.directions.map((step, i) => (
                              <div key={i} className="px-3 py-2 text-xs flex gap-2">
                                <span className="font-bold text-primary shrink-0">{step.number}.</span>
                                <span>{step.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {recipeDetail.recipe_url && (
                        <a href={recipeDetail.recipe_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                          {t('foodPicker.viewFullRecipe')}
                        </a>
                      )}
                      <p className="text-xs text-muted-foreground">{t('foodPicker.recipesNote')}</p>
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => {
                          onFoodSelected({
                            food_name: recipeDetail.name,
                            serving_size: 1,
                            serving_unit: 'serving',
                            servings: 1,
                            calories: recipeDetail.nutrition?.calories || 0,
                            protein: recipeDetail.nutrition?.protein || 0,
                            carbs: recipeDetail.nutrition?.carbs || 0,
                            fat: recipeDetail.nutrition?.fat || 0,
                          });
                          clearFoodState();
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" /> {t('foodPicker.addRecipeToMeal')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected Food Panel — fixed at bottom */}
        {selectedFood && (
          <div
            className="px-4 py-3 border-t border-border/50 shrink-0 space-y-2 bg-background"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">{selectedFood.name}</p>
                {selectedFood.brand && <p className="text-xs text-muted-foreground">{selectedFood.brand}</p>}
              </div>
              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 shrink-0" tooltip={t('common.close')} onClick={clearFoodState}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>

            {selectedFood.servings?.length > 1 && (
              <Select key={`${selectedFood.id}:${selectedServing?.serving_id || 'none'}`} value={selectedServing?.serving_id || undefined} onValueChange={handleServingChange}>
                <SelectTrigger className="text-xs h-11 md:h-8">
                  <SelectValue placeholder={t('foodPicker.selectServing')} />
                </SelectTrigger>
                <SelectContent>
                  {selectedFood.servings.map(s => (
                    <SelectItem key={s.serving_id} value={s.serving_id} className="text-xs">
                      {s.description} ({s.calories} kcal)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8" tooltip={t('food.decreaseServings')} onClick={() => setQuantity(q => Math.max(0.5, q - 0.5))}>-</Button>
                <span className="w-10 text-center text-sm font-medium">{quantity}</span>
                <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8" tooltip={t('food.increaseServings')} onClick={() => setQuantity(q => q + 0.5)}>+</Button>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 bg-muted/50 px-2 py-0.5 rounded">
                = {Math.round(((selectedServing?.metric_serving_amount ?? selectedServing?.metric_amount) || selectedFood.serving_size || 100) * quantity * 10) / 10}{(selectedServing?.metric_serving_unit ?? selectedServing?.metric_unit) || selectedFood.serving_unit || 'g'}
              </span>
            </div>

            <div className="flex gap-2 text-xs justify-between">
              <span className="font-bold text-primary">{Math.round(selectedFood.calories * quantity)} kcal</span>
              <span className="text-red-500">P: {(selectedFood.protein * quantity).toFixed(1)}g</span>
              <span className="text-yellow-500">C: {(selectedFood.carbs * quantity).toFixed(1)}g</span>
              <span className="text-blue-500">F: {(selectedFood.fat * quantity).toFixed(1)}g</span>
            </div>

            <Button onClick={handleAddFood} className="w-full bg-primary hover:bg-primary/90 h-9">
              <Plus className="h-4 w-4 mr-1" /> {t('foodPicker.addToMeal')}
            </Button>
          </div>
        )}
      </div>

      {showBarcodeScanner && (
        <BarcodeScanner
          open={showBarcodeScanner}
          onClose={() => setShowBarcodeScanner(false)}
          onScan={handleBarcodeScanned}
        />
      )}
    </>
  );
}
